import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Switch } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { fetchUsersRequest, getBranches, getRoles, getSolutions } from 'store/actions';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const CreateUser = ({ solutions, visible, handleClose, initialData = '', onSubmit }) => {
    const dispatch = useDispatch();
    const roles = useSelector(state => state?.Role?.roles);
    const branches = useSelector(state => state?.Branch?.branches);
    const [showPassword, setShowPassword] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userName: initialData?.userName || '',
            passwordHash: '',
            emailId: initialData?.emailId || '',
            phoneNo: initialData?.phoneNo || '',
            roleId: initialData?.roleId || '',
            isActive: initialData?.isActive ? 1 : 0,
            branch: initialData?.branchId || '',
            employeeId: initialData?.employeeId || '',
            solutionIds: initialData?.solutionIds
                ? initialData.solutionIds.split(',').map(id => Number(id))
                : [],
        },
        validationSchema: Yup.object({
            userName: Yup.string().required('Full Name is required'),
            passwordHash: initialData
                ? Yup.string()
                : Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
            emailId: Yup.string()
                .email('Invalid email address')
                .matches(
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    'Invalid email address format'
                )
                .required('Email is required'),
            phoneNo: Yup.string()
                .required('Phone number is required')
                .matches(/^\d{8}$/, 'Phone number must be exactly 8 digits'),
            roleId: Yup.number().required('Role is required'),
            branch: Yup.string().required('Branch is required'),
            employeeId: Yup.string().required("Employee Id is required"),
            solutionIds: Yup.array().min(1, 'At least one solution must be selected'),
        }),
        onSubmit: (values, { resetForm }) => {
            const payload = {
                sp: initialData?.userName ? 'usp_UpdateUser' : 'usp_InsertUser',
                userName: values.userName,
                emailId: values.emailId,
                phoneNo: values.phoneNo,
                roleId: values.roleId,
                passwordHash: values.passwordHash || undefined,
                isActive: values.isActive == 1 ? true : false,
                branchId: values.branch,
                employeeId: values.employeeId,
                solutionIds: values.solutionIds.join(','), // convert array to comma-separated string
                ...(initialData?.userId && { userId: initialData.userId }),
            };

            onSubmit(payload, onClose);
        },
    });

    const onClose = () => {
        formik.resetForm();
        handleClose();
    };

    useEffect(() => {
        if (visible) {
            dispatch(getBranches());
            dispatch(getRoles());
        }
    }, [visible, dispatch]);

    const roleOptions = roles?.Data?.map(role => ({
        value: role.roleId,
        label: role.roleName,
    }));

    const branchOptions = branches?.Data?.map(branch => ({
        value: branch?.branchId,
        label: branch?.branchName,
    }));

    const solutionOptions = solutions?.map(solution => ({
        value: solution.solutionId,
        label: solution.solutionName,
    }));

    const handleSelectChange = (field, selectedOption) => {
        formik.setFieldValue(field, selectedOption?.value || '');
    };

    const handleSolutionsChange = (selectedOptions) => {
        const selectedIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        formik.setFieldValue('solutionIds', selectedIds);
    };

    return (
        <Modal
            title={initialData ? 'Edit User' : 'Create New User'}
            open={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            centered
            width={500}
            className="custom-modal-header p-0"
            maskClosable={false}
        >
            <form onSubmit={formik.handleSubmit}>
                <div className="row g-3">
                    <div className="col-md-6 col-12">
                        <label className="modal-form">Full Name</label> <span className="text-danger">*</span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter full name"
                            name="userName"
                            value={formik.values.userName}
                            onChange={formik.handleChange}
                        />
                        {formik.touched.userName && formik.errors.userName && (
                            <span className="text-danger">{formik.errors.userName}</span>
                        )}
                    </div>

                    <div className="col-md-6 col-12">
                        <label className="modal-form">Employee ID</label> <span className="text-danger">*</span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter employee ID"
                            name="employeeId"
                            value={formik.values.employeeId}
                            onChange={formik.handleChange}
                        />
                        {formik.touched.employeeId && formik.errors.employeeId && (
                            <span className="text-danger">{formik.errors.employeeId}</span>
                        )}
                    </div>

                    {!initialData && (
                        <div className="col-md-6 col-12 position-relative">
                            <label className="modal-form">Password</label> <span className="text-danger">*</span>
                            <div className="input-group">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Enter password"
                                    name="passwordHash"
                                    value={formik.values.passwordHash}
                                    onChange={formik.handleChange}
                                />
                                <span
                                    className="input-group-text"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                </span>
                            </div>
                            {formik.touched.passwordHash && formik.errors.passwordHash && (
                                <span className="text-danger">{formik.errors.passwordHash}</span>
                            )}
                        </div>
                    )}

                    <div className="col-md-6 col-12">
                        <label className="modal-form">Email</label> <span className="text-danger">*</span>
                        <input
                            type="email"
                            className="form-control"
                            placeholder="Enter email"
                            name="emailId"
                            value={formik.values.emailId}
                            onChange={formik.handleChange}
                        />
                        {formik.touched.emailId && formik.errors.emailId && (
                            <span className="text-danger">{formik.errors.emailId}</span>
                        )}
                    </div>

                    <div className="col-md-6 col-12">
                        <label className="modal-form">Phone</label> <span className="text-danger">*</span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter phone number"
                            name="phoneNo"
                            value={formik.values.phoneNo}
                            onChange={formik.handleChange}
                        />
                        {formik.touched.phoneNo && formik.errors.phoneNo && (
                            <span className="text-danger">{formik.errors.phoneNo}</span>
                        )}
                    </div>

                    <div className="col-md-6 col-12">
                        <label className="modal-form">Role</label> <span className="text-danger">*</span>
                        <Select
                            name="roleId"
                            value={roleOptions?.find(option => option.value === formik.values.roleId)}
                            onChange={(opt) => handleSelectChange('roleId', opt)}
                            options={roleOptions}
                            placeholder="Select role"
                        />
                        {formik.touched.roleId && formik.errors.roleId && (
                            <span className="text-danger">{formik.errors.roleId}</span>
                        )}
                    </div>

                    <div className="col-md-6 col-12">
                        <label className="modal-form">Branch</label> <span className="text-danger">*</span>
                        <Select
                            name="branch"
                            value={branchOptions?.find(option => option.value === formik.values.branch)}
                            onChange={(opt) => handleSelectChange('branch', opt)}
                            options={branchOptions}
                            placeholder="Select branch"
                        />
                        {formik.touched.branch && formik.errors.branch && (
                            <span className="text-danger">{formik.errors.branch}</span>
                        )}
                    </div>

                    {/* Multiple Solutions Selector */}
                    <div className="col-md-6 col-12">
                        <label className="modal-form">Solutions</label> <span className="text-danger">*</span>
                        <Select
                            isMulti
                            name="solutionIds"
                            value={solutionOptions?.filter(opt => formik.values.solutionIds?.includes(Number(opt.value)))}
                            onChange={handleSolutionsChange}
                            options={solutionOptions}
                            placeholder="Select solutions"
                        />
                        {formik.touched.solutionIds && formik.errors.solutionIds && (
                            <span className="text-danger">{formik.errors.solutionIds}</span>
                        )}
                    </div>

                    <div className="col-md-6 col-12 d-flex pt-1 flex-column">
                        <label className="modal-form mb-0">Is Active</label>
                        <Switch
                            checked={formik.values.isActive === 1}
                            onChange={(checked) => formik.setFieldValue('isActive', checked ? 1 : 0)}
                            style={{ width: '50px', marginTop: '11px' }}
                        />
                    </div>
                </div>

                <div className="modal-footer mt-3">
                    <button type="button" className="btn btn-light btn-md" onClick={onClose}>
                        Close
                    </button>
                    <button type="submit" className="btn btn-primary btn-md ms-3">
                        {initialData ? 'Update' : 'Add'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

CreateUser.propTypes = {
    visible: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
};

export default CreateUser;
